import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Products.css';
import { toast } from 'react-toastify';
import { Search, Trash2, X, ImageOff, Package, BarChart3, Plus } from 'lucide-react';
import { productsAPI } from '../../config/product-api';
import { helpers } from '../../utils/helpers';
import { formatPrice, getPriceInputLabel } from '../../utils/orderDisplay';
import { ADMIN_PERMISSIONS } from '../../constants/permissions';
import { getAdminRegion } from '../../constants/stores';
import DeleteConfirmationModal from '../Common/DeleteConfirmationModal/DeleteConfirmationModal';
import { finalizeProductImageFile, createUniqueFileName } from '../../utils/imageCompression';
import {
  buildProductImagesSubmitPayload,
  buildVariantsSubmitPayload,
  mapProductImagesFromApi,
  mapProductVariantsFromApi,
  validateVariants,
} from '../../utils/productVariants';
import ProductVariantsEditor from './ProductVariantsEditor';
import ImageCropperModal from '../Common/ImageCropperModal/ImageCropperModal';
import { PRODUCT_IMAGE_ASPECT_RATIO } from '../../constants/productImage';

const MAX_PRODUCT_IMAGES = 10;

const emptyForm = {
  name: '',
  description: '',
  price: '',
  stock: '0',
  isAvailable: true,
  images: [],
  productId: null,
  variants: [],
  region: 'qatar',
};

const revokeBlobUrls = (images = []) => {
  images.forEach((item) => {
    if (item?.url?.startsWith('blob:')) {
      URL.revokeObjectURL(item.url);
    }
  });
};

const Products = ({ adminData, openProductId = null, onOpenProductConsumed }) => {
  const navigate = useNavigate();
  const storeRegion = getAdminRegion(adminData);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [isCompressingImage, setIsCompressingImage] = useState(false);
  const [cropQueue, setCropQueue] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, productId: null, productName: '' });
  const [isDeleting, setIsDeleting] = useState(false);

  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_PRODUCTS);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_PRODUCTS);
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_PRODUCTS);
  const canView = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_PRODUCTS);
  const canModifyForm = (!editMode && canCreate) || (editMode && canEdit);
  const canViewOrders = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ORDERS);
  const canViewReports = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_REPORTS)
    || helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ORDERS);
  const canEditStock = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_STOCK);

  const handleViewOrders = () => {
    if (!selectedProductId || !formData.name) return;
    const filter = {
      productId: selectedProductId,
      productNumericId: formData.productId,
      productName: formData.name,
    };
    handleCloseDialog();
    navigate('/dashboard', {
      state: {
        page: 'orders',
        productFilter: filter,
      },
    });
  };

  const handleViewDailySaleReport = () => {
    if (!selectedProductId || !formData.name) return;
    const filter = {
      productId: selectedProductId,
      productNumericId: formData.productId,
      productName: formData.name,
    };
    handleCloseDialog();
    navigate('/dashboard', {
      state: {
        page: 'reports',
        productFilter: filter,
      },
    });
  };

  const fetchProducts = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await productsAPI.getProductsList({
        page,
        limit: 20,
        search: searchQuery || undefined,
        isAvailable: statusFilter || undefined,
        region: storeRegion,
      });

      if (response.success) {
        setProducts(response.data || []);
        setPagination(response.pagination || pagination);
      } else {
        setError(response.error || 'Failed to fetch products');
      }
    } catch (err) {
      setError('Error fetching products: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isDialogOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDialogOpen]);

  useEffect(() => {
    if (canView) {
      fetchProducts(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, currentPage, searchQuery, statusFilter, storeRegion]);

  useEffect(() => {
    if (!openProductId || !canView) return;

    let cancelled = false;

    const openFromList = async () => {
      const fromList = products.find((p) => String(p._id) === String(openProductId));
      if (fromList) {
        if (!cancelled) {
          handleEdit(fromList);
          onOpenProductConsumed?.();
        }
        return;
      }

      // Wait for the first products page load before falling back to a full fetch.
      if (loading) return;

      try {
        const response = await productsAPI.getProductsList({ page: 1, limit: 500, region: storeRegion });
        if (cancelled) return;
        if (response.success) {
          const match = (response.data || []).find((p) => String(p._id) === String(openProductId));
          if (match) {
            handleEdit(match);
          } else {
            toast.error('Product not found');
          }
        } else {
          toast.error(response.error || 'Failed to open product details');
        }
      } catch {
        if (!cancelled) toast.error('Failed to open product details');
      } finally {
        if (!cancelled) onOpenProductConsumed?.();
      }
    };

    openFromList();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openProductId, canView, products, loading]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchQuery(searchInput.trim());
  };

  const handleResetSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'stock') {
      const cleaned = value === '' ? '' : value.replace(/[^\d]/g, '');
      setFormData((prev) => ({ ...prev, stock: cleaned }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddNew = () => {
    if (!canCreate) return;
    setEditMode(false);
    setSelectedProductId(null);
    setFormData({ ...emptyForm, region: storeRegion });
    setIsDialogOpen(true);
  };

  const handleEdit = (product) => {
    if (!canView) return;
    setEditMode(true);
    setSelectedProductId(product._id);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.rate !== undefined ? product.rate.toString() : '',
      stock: product.stock !== undefined && product.stock !== null ? String(product.stock) : '0',
      isAvailable: product.isAvailable !== false,
      images: mapProductImagesFromApi(product.images),
      productId: product.productId || null,
      variants: mapProductVariantsFromApi(product.variants),
      region: product.region === 'india' ? 'india' : 'qatar',
    });
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (product) => {
    if (!canDelete) return;
    setDeleteModal({
      isOpen: true,
      productId: product._id,
      productName: product.name || 'this product',
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.productId) return;

    const deletedProductId = deleteModal.productId;
    const shouldCloseDialog = isDialogOpen && selectedProductId === deletedProductId;

    try {
      setIsDeleting(true);
      const response = await productsAPI.deleteProduct(deletedProductId);
      if (response.success) {
        toast.success('Product deleted successfully');
        setDeleteModal({ isOpen: false, productId: null, productName: '' });
        if (shouldCloseDialog) {
          handleCloseDialog();
        }
        fetchProducts(currentPage);
      } else {
        toast.error(response.error || 'Failed to delete product');
      }
    } catch {
      toast.error('An error occurred while deleting the product');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDialog = () => {
    revokeBlobUrls(formData.images);
    setCropQueue(null);
    setIsCompressingImage(false);
    setIsDialogOpen(false);
    setEditMode(false);
    setSelectedProductId(null);
    setFormData(emptyForm);
  };

  const openNextCrop = (files) => {
    const [first, ...rest] = files;
    if (!first) {
      setCropQueue(null);
      setIsCompressingImage(false);
      return;
    }
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      setCropQueue({
        // Fresh key forces a clean cropper remount for each selected image.
        key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        src: readerEvent.target.result,
        remaining: rest,
      });
    };
    reader.readAsDataURL(first);
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || cropQueue || isCompressingImage) return;

    const currentCount = formData.images?.length || 0;
    if (currentCount + files.length > MAX_PRODUCT_IMAGES) {
      toast.error(`Maximum ${MAX_PRODUCT_IMAGES} images allowed`);
      return;
    }

    const invalid = files.find((file) => !file.type?.startsWith('image/') || file.size > 8 * 1024 * 1024);
    if (invalid && !invalid.type?.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (invalid) {
      toast.error('Each source image must be less than 8MB');
      return;
    }

    setIsCompressingImage(true);
    openNextCrop(files);
  };

  const handleCropperClose = () => {
    setCropQueue(null);
    setIsCompressingImage(false);
  };

  const handleCropComplete = async (croppedFile) => {
    if (!cropQueue) return;
    const { remaining } = cropQueue;
    setCropQueue(null);
    setIsCompressingImage(true);
    try {
      const finalImage = await finalizeProductImageFile(croppedFile);
      const namedImage = new File(
        [finalImage],
        createUniqueFileName(finalImage.name || 'product.jpg'),
        { type: finalImage.type || 'image/jpeg', lastModified: Date.now() }
      );
      const previewUrl = URL.createObjectURL(namedImage);
      setFormData((prev) => {
        const current = prev.images || [];
        return {
          ...prev,
          images: [
            ...current,
            {
              type: 'new',
              mediaType: 'image',
              url: previewUrl,
              thumbnail: previewUrl,
              file: namedImage,
              order: String(current.length),
            },
          ],
        };
      });
      if (remaining.length) {
        openNextCrop(remaining);
      } else {
        setIsCompressingImage(false);
      }
    } catch (err) {
      setIsCompressingImage(false);
      toast.error(err.message || 'Failed to process image');
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => {
      const mediaItem = prev.images?.[index];
      if (mediaItem?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(mediaItem.url);
      }
      return {
        ...prev,
        images: (prev.images || []).filter((_, i) => i !== index),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editMode && !canEdit) return;
    if (!editMode && !canCreate) return;

    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }

    if (formData.price === '' || isNaN(Number(formData.price)) || Number(formData.price) <= 0) {
      toast.error('Price must be a valid number greater than 0');
      return;
    }

    if (canEditStock) {
      const stockNum = Number(formData.stock);
      if (formData.stock === '' || isNaN(stockNum) || stockNum < 0 || !Number.isInteger(stockNum)) {
        toast.error('Stock must be a whole number of 0 or more');
        return;
      }
    }

    const variantError = validateVariants(formData.variants || []);
    if (variantError) {
      toast.error(variantError);
      return;
    }

    setSubmitting(true);
    try {
      const { imagesData, productImageFiles } = buildProductImagesSubmitPayload(formData.images || []);
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        rate: Number(formData.price),
        isAvailable: formData.isAvailable,
        imagesData,
        productImageFiles,
        region: storeRegion,
      };

      if (canEditStock) {
        payload.stock = Math.max(0, Math.floor(Number(formData.stock)) || 0);
      }

      if (editMode || (formData.variants || []).length > 0) {
        const { variantsData, variantImageFiles } = buildVariantsSubmitPayload(formData.variants || []);
        payload.variantsData = variantsData;
        payload.variantImageFiles = variantImageFiles;
      }

      let result;
      if (editMode) {
        result = await productsAPI.updateProduct(selectedProductId, payload);
      } else {
        result = await productsAPI.createProduct(payload);
      }

      if (result.success) {
        toast.success(editMode ? 'Product updated successfully' : 'Product created successfully');
        handleCloseDialog();
        fetchProducts(currentPage);
      } else {
        toast.error(result.error || (editMode ? 'Failed to update product' : 'Failed to create product'));
      }
    } catch (err) {
      toast.error(`An error occurred while ${editMode ? 'updating' : 'creating'} the product`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!canView) {
    return (
      <div className="productlist-page">
        <div className="productlist-empty-state">
          <p>You do not have permission to view products.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="productlist-page">
      <div className="productlist-page-header">
        <div className="productlist-page-title">
          <h1>Products</h1>
          <p>View and manage all your products</p>
        </div>
        {canCreate && (
          <button className="productlist-create-btn" onClick={handleAddNew}>
            Add Product
          </button>
        )}
      </div>

      <div className="productlist-search-bar-container">
        <form className="productlist-search-form" onSubmit={handleSearch}>
          <select
            className="productlist-status-filter"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Statuses</option>
            <option value="true">In Stock</option>
            <option value="false">Out of Stock</option>
          </select>
          <div className="productlist-search-input-wrapper">
            <Search size={18} className="productlist-search-icon" />
            <input
              type="text"
              className="productlist-search-input"
              placeholder="Search by name or description..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <button type="submit" className="productlist-search-btn">Search</button>
          {searchQuery && (
            <button type="button" className="productlist-reset-btn" onClick={handleResetSearch}>
              <X size={18} />
            </button>
          )}
        </form>
      </div>

      <div className="productlist-table-container">
        {loading ? (
          <div className="productlist-loading-state">
            <div className="productlist-loading-spinner" />
            <p>Loading products...</p>
          </div>
        ) : error ? (
          <div className="productlist-error-state">
            <p>{error}</p>
            <button onClick={() => fetchProducts(currentPage)} className="productlist-retry-button">
              Retry
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="productlist-empty-state">
            <p>No products found</p>
          </div>
        ) : (
          <>
            <div className="productlist-table-wrapper">
              <table className="productlist-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Product ID</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th className="productlist-stock-cell">Stock</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const thumbnailUrl = product.images?.[0]?.thumbnail || product.images?.[0]?.image || '';
                    return (
                      <tr key={product._id}>
                        <td className="productlist-pic-cell">
                          <div className="productlist-table-pic-wrapper">
                            {thumbnailUrl ? (
                              <img
                                src={thumbnailUrl}
                                alt={product.name}
                                className="productlist-table-pic"
                              />
                            ) : (
                              <ImageOff size={20} className="productlist-table-pic-fallback" />
                            )}
                          </div>
                        </td>
                        <td className="productlist-id-cell">
                          {canView ? (
                            <span className="productlist-id-link" onClick={() => handleEdit(product)}>
                              {product.productId}
                            </span>
                          ) : (
                            <span>{product.productId}</span>
                          )}
                        </td>
                        <td className="productlist-name-cell">
                          {product.name}
                        </td>
                        <td>
                          <span className={`productlist-status-badge ${product.isAvailable !== false ? 'active' : 'inactive'}`}>
                            {product.isAvailable !== false ? 'In Stock' : 'Out of Stock'}
                          </span>
                        </td>
                        <td className="productlist-stock-cell">{product.stock ?? 0}</td>
                        <td className="productlist-price-cell">{formatPrice(product.rate || 0, adminData)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="productlist-pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev || loading}
                  className="productlist-pagination-button"
                >
                  Previous
                </button>
                <span className="productlist-pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext || loading}
                  className="productlist-pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isDialogOpen && ((editMode && canView) || (!editMode && canCreate)) && (
        <div className="productlist-dialog-overlay">
          <div className="productlist-dialog-box">
            <div className="productlist-dialog-header">
              <button type="button" className="productlist-close-btn" onClick={handleCloseDialog} aria-label="Close">&times;</button>
              <div className="productlist-dialog-header-row">
                <div className="productlist-dialog-title-section">
                  <h2>
                    {editMode
                      ? `${canEdit ? 'Edit Product' : 'Product Details'}${formData.name ? ` - ${formData.name}` : ''}`
                      : 'Add Product'}
                  </h2>
                  {editMode && formData.productId && (
                    <p className="productlist-dialog-subtitle">Product ID: {formData.productId}</p>
                  )}
                </div>
                <div className="availability-toggle-container productlist-dialog-status">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      checked={formData.isAvailable}
                      onChange={handleInputChange}
                      disabled={!canEdit || submitting}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`availability-text ${formData.isAvailable ? 'in-stock' : 'out-of-stock'}`}>
                    {formData.isAvailable ? 'In Stock' : 'Out of Stock'}
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="productlist-form">
              <span className="productlist-section-label">Product Images (1:1, max 1MB each)</span>
              <div className="productlist-image-section">
                <div className="productlist-images-grid">
                  {(formData.images || []).map((media, index) => (
                    <div key={`product-image-${index}`} className="productlist-image-item">
                      <img src={media.thumbnail || media.url} alt={`Product ${index + 1}`} />
                      {canModifyForm && (
                        <button
                          type="button"
                          className="productlist-image-remove"
                          onClick={() => handleRemoveImage(index)}
                          disabled={submitting || isCompressingImage}
                          title="Remove image"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {canModifyForm && (formData.images || []).length < MAX_PRODUCT_IMAGES && (
                    <label
                      className={`productlist-image-add ${isCompressingImage ? 'loading' : ''}`}
                      title="Add image"
                    >
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageSelect}
                        disabled={!canModifyForm || isCompressingImage || submitting}
                        style={{ display: 'none' }}
                      />
                      {isCompressingImage ? '...' : <Plus size={28} />}
                    </label>
                  )}
                  {!canModifyForm && !(formData.images || []).length && (
                    <div className="productlist-image-item productlist-image-empty">
                      <ImageOff size={28} />
                      <span>No images</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="productlist-form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Product name"
                  required
                  disabled={!canEdit || submitting}
                  readOnly={!canEdit}
                />
              </div>

              <div className="productlist-form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Product description..."
                  rows={3}
                  disabled={!canEdit || submitting}
                  readOnly={!canEdit}
                />
              </div>

              <div className="productlist-form-row">
                <div className="productlist-form-group productlist-form-group-half">
                  <label htmlFor="price">{getPriceInputLabel(adminData)} *</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    step="0.01"
                    min="0.01"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    required
                    disabled={!canEdit || submitting}
                    readOnly={!canEdit}
                  />
                </div>

                <div className="productlist-form-group productlist-form-group-half">
                  <label htmlFor="stock">Stock{canEditStock ? '' : ' (view only)'}</label>
                  <input
                    type="number"
                    id="stock"
                    name="stock"
                    step="1"
                    min="0"
                    value={formData.stock}
                    onChange={handleInputChange}
                    placeholder="0"
                    disabled={!canEditStock || submitting}
                    readOnly={!canEditStock}
                  />
                </div>
              </div>

              <ProductVariantsEditor
                variants={formData.variants || []}
                onChange={(variants) => setFormData((prev) => ({ ...prev, variants }))}
                disabled={submitting || isCompressingImage || !canEdit}
                basePrice={formData.price}
                adminData={adminData}
              />

               <div className="productlist-form-actions">
                 <div className="productlist-form-actions-left">
                   {editMode && canViewOrders && (
                     <button
                       type="button"
                       className="productlist-view-orders-btn"
                       onClick={handleViewOrders}
                       disabled={submitting}
                     >
                       <Package size={16} />
                       View Orders
                     </button>
                   )}
                   {editMode && canViewReports && (
                     <button
                       type="button"
                       className="productlist-view-orders-btn productlist-view-report-btn"
                       onClick={handleViewDailySaleReport}
                       disabled={submitting}
                     >
                       <BarChart3 size={16} />
                       View Daily Sale Report
                     </button>
                   )}
                   {editMode && canDelete && (
                     <button
                       type="button"
                       className="productlist-delete-dialog-btn"
                       onClick={() => handleDeleteClick({ _id: selectedProductId, name: formData.name })}
                       disabled={submitting}
                     >
                       <Trash2 size={16} />
                       Delete Product
                     </button>
                   )}
                 </div>
                 <button type="button" className="productlist-cancel-btn" onClick={handleCloseDialog} disabled={submitting}>
                   Cancel
                 </button>
                 {(!editMode || canEdit) && (
                   <button type="submit" className="productlist-submit-btn" disabled={submitting || (editMode && !canEdit)}>
                     {submitting ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Product' : 'Create Product')}
                   </button>
                 )}
               </div>
            </form>
          </div>
        </div>
      )}

      {cropQueue?.src && (
        <ImageCropperModal
          key={cropQueue.key || cropQueue.src}
          imageSrc={cropQueue.src}
          aspectRatio={PRODUCT_IMAGE_ASPECT_RATIO}
          title="Crop product image (1:1)"
          onClose={handleCropperClose}
          onCropComplete={handleCropComplete}
        />
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, productId: null, productName: '' })}
        onConfirm={handleDeleteConfirm}
        title="Delete Product"
        message={`Are you sure you want to delete "${deleteModal.productName}"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Products;
